"use strict";
/**
 * Row-level attribution — the one place the shapes and the exemptions live (`RPM-054`).
 *
 * `docs/rpm/03-RPM-SCHEMA.md` §9.1 states the obligation: every row says who put it there,
 * `created_by` is never null and never a shared login, and a non-human writer resolves to a
 * `service_principals` row naming its kind and the human accountable for it. §9.6 enumerates the
 * shapes, one per table. §8.5 names the check that reads them, `TABLE_WITHOUT_ATTRIBUTION`, and
 * says where they belong: *"the shapes are §9.6's and live in code, not in a reviewer's memory."*
 * This file is that code, and `attribution.test.ts` plus
 * `apps/server/test/integration/audit/attribution.integration.test.ts` are what read it.
 *
 * Two things this file deliberately does NOT decide:
 *
 *   - whether a table declared `change-trail` carries a `config_entity_registries` row and a
 *     trigger. That is `TABLE_NOT_ENROLLED` (§8.5), it is `RPM-055`'s acceptance rows 4 and 5, and
 *     `D-227` already ruled that which tables join the trail is a separate design decision. Seven
 *     tables are enrolled since CR-43 — the facility profile and the six routing tables, each
 *     declared below by its column shape — and none of the seven declared `change-trail` is among
 *     them; declaring those covered would claim a trail that does not exist, and claiming one is
 *     worse than not having one.
 *   - which tables exist. The check is handed the catalogue and compares it against the
 *     declaration, so a table added to the database and not to §9.6 is named rather than assumed.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ATTRIBUTION_INPUT_FIELDS = exports.ATTRIBUTION_EXCLUDED_TABLES = exports.ATTRIBUTION_SHAPES = void 0;
exports.findTablesWithoutAttribution = findTablesWithoutAttribution;
exports.declaredButNotDeployed = declaredButNotDeployed;
exports.findAttributionInputFields = findAttributionInputFields;
/**
 * §9.6's coverage table, transcribed. Thirty-nine entries, one per table, and every table this
 * domain builds is in exactly one of them: CR-43 took out the two policy tables and put in the six
 * routing tables, the resolution log and the two summary tables, and CR-45 added the table of
 * time on the entry (`rpm_entry_time_ticks`, `RPM-106`); `J-695` and `J-690` added
 * `account_origins` and `platform_impersonations` (`D-1688`).
 *
 * Every one of the thirty-nine is deployed. `rpm_alert_notify_people` was the exception — §9.6
 * declared it and no migration created it (`D-607`) — until `J-413` built it in
 * `db/rpm-clinical.ts` and migration `0022` created it. Its entry below is therefore exercised
 * rather than tolerated: `findTablesWithoutAttribution` reads it against the catalogue on every
 * integration run, and `declaredButNotDeployed` now returns nothing. The check still operates on
 * the intersection with the catalogue and still reports the difference in both directions, so a
 * table declared here and dropped from the database is named rather than assumed.
 */
exports.ATTRIBUTION_SHAPES = [
    // ---- §9.6 row 1: created_at / created_by, plus updated_at / updated_by (14) -----------------
    ...[
        "role_assignments",
        "service_principals",
        "source_systems",
        "vital_sign_definitions",
        "rpm_facility_profiles",
        "rpm_facility_routing",
        "rpm_care_units",
        "rpm_routing_contacts",
        "rpm_routing_endpoints",
        "rpm_standing_ccs",
        "rpm_notification_attributes",
        "vital_reading_groups",
        "rpm_alerts",
        "held_calls",
    ].map((table) => ({
        table,
        shape: "created-updated",
        columns: ["created_by", "updated_by"],
        why: "Edited in place, so both the writer and the last editor are facts worth keeping.",
    })),
    // ---- §9.6 row 2: created_at / created_by, no updater (7) ------------------------------------
    ...[
        "record_access_events",
        "classification_band_sets",
        "classification_bands",
        "vital_readings",
        "rpm_alert_notify_people",
        "rpm_facility_summaries",
        "rpm_facility_summary_alerts",
    ].map((table) => ({
        table,
        shape: "created",
        columns: ["created_by"],
        why: "Never edited in place — append-only, or superseded by a successor row rather than rewritten.",
    })),
    // ---- §9.6 row 3, first half: a mandatory named actor of its own (9) -------------------------
    ...[
        ["break_glass_events", "user_id"],
        ["rpm_alert_state_transitions", "actor_user_id"],
        ["notification_attempts", "attempted_by"],
        ["held_call_alerts", "attached_by"],
        ["admin_change_events", "actor_user_id"],
        ["rpm_alert_routing_resolutions", "resolved_by"],
        ["rpm_entry_time_ticks", "counted_by"],
        // J-695 and J-690 (D-1688): who made the account, and the operator who impersonated.
        ["account_origins", "made_by_user_id"],
        ["platform_impersonations", "operator_user_id"],
    ].map(([table, column]) => ({
        table,
        shape: "named-actor",
        columns: [column],
        why: "The actor IS the fact the row records. A created_by beside it is the same person under two names.",
    })),
    // ---- §9.6 row 3, second half: attributed by the alert they hang from (2) --------------------
    ...[
        ["alert_note_sections", "edited_by"],
        ["alert_action_items", "touched_by"],
    ].map(([table]) => ({
        table,
        shape: "parent-attributed",
        columns: [],
        parent: { table: "rpm_alerts", via: "rpm_alert_id" },
        why: "Exists only under an rpm_alerts that carries created_by; its own stamp records the first human change, so it is nullable by design.",
    })),
    // ---- §9.6 row 4: the change trail (7) ------------------------------------------------------
    ...[
        "tenants",
        "practices",
        "practice_configurations",
        "people",
        "person_tenant_affiliations",
        "minimum_necessary_declarations",
        "config_entity_registries",
    ].map((table) => ({
        table,
        shape: "change-trail",
        columns: [],
        why: "Bound to TENANCY §6.2/§6.4 column-for-column (N4) and enrolled in admin_change_events, which records the actor of every insert and update.",
    })),
];
/**
 * The eight tables in `public` this specification did not write, excluded by name.
 *
 * §9.3: *"The gate excludes those eight by name, and the exclusion is enumerated rather than
 * inferred so that a ninth pre-existing table is a decision somebody makes rather than a silent
 * widening."* Adding a ninth entry here is that decision, and it needs a journal line.
 */
exports.ATTRIBUTION_EXCLUDED_TABLES = {
    user: "Better Auth owns its shape and N17 says it is not modified.",
    session: "Better Auth owns its shape and N17 says it is not modified.",
    account: "Better Auth owns its shape and N17 says it is not modified.",
    verification: "Better Auth owns its shape and N17 says it is not modified.",
    two_factor: "Better Auth owns its shape and N17 says it is not modified.",
    rate_limit: "ChartVN's own, predates this domain; enrolling it would write a row per rate-limit bump.",
    audit_events: "The repository's table. It carries actor_user_id, is append-only and hash-chained, and has its own controls (T12).",
    sample_records: "ChartVN's own demonstration table, predates this domain.",
};
/**
 * `TABLE_WITHOUT_ATTRIBUTION` (§8.5). Given the base tables in `public` and their columns, returns
 * one finding per table that is not attributable, naming the table.
 *
 * Four ways a table is named:
 *
 *   1. it is in scope and §9.6 declares no shape for it — the new-table case row 3 of `RPM-054`
 *      states, and the reason this check exists;
 *   2. its declared column is absent;
 *   3. its declared column is nullable, so the row can carry no actor at all;
 *   4. it is `parent-attributed` and the parent link is absent, nullable, or points at a table
 *      that is not itself attributable.
 */
function findTablesWithoutAttribution(catalogue) {
    const declared = new Map(exports.ATTRIBUTION_SHAPES.map((d) => [d.table, d]));
    const byTable = new Map();
    for (const c of catalogue.columns) {
        let cols = byTable.get(c.table);
        if (!cols) {
            cols = new Map();
            byTable.set(c.table, cols);
        }
        cols.set(c.column, c);
    }
    const inScope = catalogue.tables.filter((t) => !Object.hasOwn(exports.ATTRIBUTION_EXCLUDED_TABLES, t));
    const carriesAttribution = (table) => {
        const d = declared.get(table);
        if (!d)
            return false;
        if (d.shape === "change-trail")
            return true;
        if (d.shape === "parent-attributed")
            return true;
        return d.columns.every((name) => byTable.get(table)?.get(name)?.nullable === false);
    };
    const findings = [];
    for (const table of inScope) {
        const d = declared.get(table);
        if (!d) {
            findings.push({
                table,
                code: "TABLE_WITHOUT_ATTRIBUTION",
                detail: `no shape is declared for it; §9.6 enumerates the four and every table is in exactly one`,
            });
            continue;
        }
        for (const name of d.columns) {
            const col = byTable.get(table)?.get(name);
            if (!col) {
                findings.push({
                    table,
                    code: "TABLE_WITHOUT_ATTRIBUTION",
                    detail: `declared shape "${d.shape}" needs column ${name}, which the table does not have`,
                });
            }
            else if (col.nullable) {
                findings.push({
                    table,
                    code: "TABLE_WITHOUT_ATTRIBUTION",
                    detail: `column ${name} is nullable, so a row can carry no actor; §9.1 makes it NOT NULL on every table`,
                });
            }
        }
        if (d.shape === "parent-attributed") {
            const link = d.parent === undefined ? undefined : byTable.get(table)?.get(d.parent.via);
            if (d.parent === undefined || !link || link.nullable) {
                findings.push({
                    table,
                    code: "TABLE_WITHOUT_ATTRIBUTION",
                    detail: `declared shape "parent-attributed" needs a NOT NULL link to its parent, which the table does not have`,
                });
            }
            else if (!carriesAttribution(d.parent.table)) {
                findings.push({
                    table,
                    code: "TABLE_WITHOUT_ATTRIBUTION",
                    detail: `its parent ${d.parent.table} is not attributable, so neither is it`,
                });
            }
        }
    }
    return findings;
}
/** Tables §9.6 declares that the catalogue does not hold. Information, not a finding. */
function declaredButNotDeployed(tables) {
    const deployed = new Set(tables);
    return exports.ATTRIBUTION_SHAPES.map((d) => d.table).filter((t) => !deployed.has(t));
}
/* ---- the contract half: no route accepts attribution as input (`RPM-054` row 4) -------------- */
/**
 * The field names no request input may declare, in both spellings the stack uses: `camelCase` on
 * the wire and in Drizzle, `snake_case` in the database.
 *
 * §6's API row for `RPM-054`: *"Attribution is supplied by the repository layer from the request
 * context; no route accepts it as input. A route that did could be lied to."* The set is the
 * attribution columns §9.6 declares, taken as the pairs §9.1 states them in — `created_at` with
 * `created_by`, `updated_at` with `updated_by` — plus the named actor columns and the two
 * separately-named actors §4 declares, `published_by` and `granted_by_user_id`.
 *
 * `user_id` is NOT in this set, and that is deliberate rather than an oversight. It is
 * `break_glass_events`' attribution column, and it is also the SUBJECT of `role_assignments`,
 * `session` and `account` — a check that refused the name everywhere would refuse correct schemas,
 * which is how a check gets switched off. The invoker of a break-glass still comes from the
 * session; what this check cannot do is police that through the name alone.
 */
exports.ATTRIBUTION_INPUT_FIELDS = [
    "created_at",
    "createdAt",
    "created_by",
    "createdBy",
    "updated_at",
    "updatedAt",
    "updated_by",
    "updatedBy",
    "actor_user_id",
    "actorUserId",
    "attempted_by",
    "attemptedBy",
    "attached_by",
    "attachedBy",
    "edited_by",
    "editedBy",
    "touched_by",
    "touchedBy",
    "published_by",
    "publishedBy",
    "granted_by_user_id",
    "grantedByUserId",
];
const isObject = (v) => typeof v === "object" && v !== null;
/**
 * Every property name a schema can reach, following `$ref` into `components.schemas` and through
 * `properties`, `items`, `allOf`, `anyOf`, `oneOf` and `additionalProperties`.
 */
function propertyNames(schema, doc, seen) {
    if (!isObject(schema) || seen.has(schema))
        return [];
    seen.add(schema);
    const out = [];
    const ref = schema.$ref;
    if (typeof ref === "string" && ref.startsWith("#/")) {
        let node = doc;
        for (const part of ref.slice(2).split("/")) {
            node = isObject(node) ? node[part.replace(/~1/g, "/").replace(/~0/g, "~")] : undefined;
        }
        out.push(...propertyNames(node, doc, seen));
    }
    if (isObject(schema.properties)) {
        for (const [name, sub] of Object.entries(schema.properties)) {
            out.push(name, ...propertyNames(sub, doc, seen));
        }
    }
    for (const key of ["items", "additionalProperties", "not"]) {
        out.push(...propertyNames(schema[key], doc, seen));
    }
    for (const key of ["allOf", "anyOf", "oneOf"]) {
        const branch = schema[key];
        if (Array.isArray(branch))
            for (const sub of branch)
                out.push(...propertyNames(sub, doc, seen));
    }
    return out;
}
const HTTP_METHODS = ["get", "put", "post", "delete", "options", "head", "patch", "trace"];
/**
 * `RPM-054` row 4. Walks every operation's inputs — parameters and request bodies, never responses —
 * and reports each `ATTRIBUTION_INPUT_FIELDS` name it finds.
 *
 * Responses are excluded on purpose: `created_at` leaving the server is the record of when the row
 * was written, and `userPublicSchema` already returns one. The rule is about what a caller may
 * send, not about what it may read.
 */
function findAttributionInputFields(document) {
    const doc = isObject(document) ? document : {};
    const banned = new Set(exports.ATTRIBUTION_INPUT_FIELDS);
    const findings = [];
    let inspected = 0;
    const paths = isObject(doc.paths) ? doc.paths : {};
    for (const [path, item] of Object.entries(paths)) {
        if (!isObject(item))
            continue;
        for (const method of HTTP_METHODS) {
            const op = item[method];
            if (!isObject(op))
                continue;
            const operation = `${method.toUpperCase()} ${path}`;
            const params = Array.isArray(op.parameters) ? op.parameters : [];
            for (const p of params) {
                if (!isObject(p))
                    continue;
                inspected += 1;
                const where = typeof p.in === "string" ? p.in : "parameter";
                const name = typeof p.name === "string" ? p.name : "";
                const names = [name, ...propertyNames(p.schema, doc, new Set())].filter(Boolean);
                for (const field of names) {
                    if (banned.has(field))
                        findings.push({ operation, location: `${where} parameter`, field });
                }
            }
            const body = op.requestBody;
            if (isObject(body) && isObject(body.content)) {
                for (const [media, entry] of Object.entries(body.content)) {
                    if (!isObject(entry))
                        continue;
                    inspected += 1;
                    for (const field of propertyNames(entry.schema, doc, new Set())) {
                        if (banned.has(field))
                            findings.push({ operation, location: `request body (${media})`, field });
                    }
                }
            }
        }
    }
    return { findings, inspected };
}
//# sourceMappingURL=attribution.js.map